import { useQuery, useMutation } from '@apollo/client/react'
import {
  GET_ADMIN_USERS,
  GET_WAITLIST,
  APPROVE_WAITLIST,
  DISABLE_USER,
  ADMIN_UPDATE_ACCOUNT,
  GET_SYSTEM_PROMPTS,
  UPSERT_SYSTEM_PROMPT,
  DELETE_SYSTEM_PROMPT,
} from '@/lib/operations/admin'
import type { WaitlistEntry, SystemPrompt, UserAccount } from '@/lib/types'

// ─── Read hooks ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  const { data, loading, error } = useQuery<{ adminUsers: UserAccount[] }>(GET_ADMIN_USERS)
  return { users: data?.adminUsers ?? [], loading, error }
}

export function useWaitlist() {
  const { data, loading, error } = useQuery<{ adminWaitlist: WaitlistEntry[] }>(GET_WAITLIST)
  return { waitlist: data?.adminWaitlist ?? [], loading, error }
}

export function useSystemPrompts() {
  const { data, loading, error } = useQuery<{ adminSystemPrompts: SystemPrompt[] }>(GET_SYSTEM_PROMPTS)
  return { systemPrompts: data?.adminSystemPrompts ?? [], loading, error }
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────

export function useApproveWaitlist() {
  const [mutate, { loading }] = useMutation<{ approveWaitlist: boolean }>(APPROVE_WAITLIST, {
    refetchQueries: [{ query: GET_WAITLIST }],
  })
  const approve = (docId: string, approved: boolean) =>
    mutate({ variables: { docId, approved } })
  return { approve, loading }
}

export function useDisableUser() {
  const [mutate, { loading }] = useMutation<{ disableUser: boolean }>(DISABLE_USER, {
    refetchQueries: [{ query: GET_ADMIN_USERS }],
  })
  const disableUser = (uid: string, disabled: boolean) =>
    mutate({ variables: { uid, disabled } })
  return { disableUser, loading }
}

export function useAdminUpdateAccount() {
  const [mutate, { loading }] = useMutation<{ adminUpdateAccount: { uid: string; planType: string; maxClients: number; disabledAt: string | null; updatedAt: string } }>(ADMIN_UPDATE_ACCOUNT, {
    refetchQueries: [{ query: GET_ADMIN_USERS }],
  })
  const adminUpdate = (
    uid: string,
    input: { planType?: string; maxClients?: number; disabledAt?: string },
  ) => mutate({ variables: { uid, ...input } })
  return { adminUpdate, loading }
}

export function useUpsertSystemPrompt() {
  const [mutate, { loading }] = useMutation<{ upsertSystemPrompt: SystemPrompt }>(UPSERT_SYSTEM_PROMPT, {
    refetchQueries: [{ query: GET_SYSTEM_PROMPTS }],
  })
  const upsert = (key: string, content: string) =>
    mutate({ variables: { key, content } })
  return { upsert, loading }
}

export function useDeleteSystemPrompt() {
  const [mutate, { loading }] = useMutation<{ deleteSystemPrompt: boolean }>(DELETE_SYSTEM_PROMPT, {
    refetchQueries: [{ query: GET_SYSTEM_PROMPTS }],
  })
  const deletePrompt = (key: string) => mutate({ variables: { key } })
  return { deletePrompt, loading }
}
